import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ReplaySubject, firstValueFrom, lastValueFrom } from 'rxjs';
import { HubService } from './hub.service';
import { Auth } from '@angular/fire/auth';
import { PaymentData } from '../model/common.model';
import { LoyaltyService } from './loyalty.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private http: HttpClient, private hubService: HubService, private auth: Auth, private loyaltyService: LoyaltyService,
    @Inject(PLATFORM_ID) private platformId) {
    if (isPlatformBrowser(this.platformId)) {
      auth.onAuthStateChanged(async (user) => {
        this.refresh(user);
      });
      auth.onIdTokenChanged(async (user) => {
        this.refresh(user);
      });
    }
  }

  async getToken() {
    try {
      return await this.auth.currentUser?.getIdToken();
    } catch (error: any) {
      const code = error?.code || '';
      if (code === 'auth/user-token-expired' || code === 'auth/invalid-user-token') await this.auth.signOut();
      return null;
    }
  }

  current$ = new ReplaySubject<any>(1);
  get currentFirebaseUser() { return this.auth.currentUser; }
  current() {
    if (!this.auth.currentUser) this.current$.next(null);
    else setTimeout(async () => {
      try {
        const user = await firstValueFrom(this.http.get<any>('/api/user/current'));

        this.leave();
        this.current$.next(user);
        if (user?.Id) this.hubService.join(user.Id);
      }
      catch { this.current$.next(null); }
    });
  }

  refreshing = false;
  async refresh(user: any) {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      this.current();
        await this.loyaltyService.current();
    } finally { delete this.refreshing; }
  }

  ngOnDestroy() { this.leave(); }
  private leave() {
    lastValueFrom(this.current$).then(current => current?.Id && this.hubService.leave(current?.Id));
  }

  // permissions() {
  //   return firstValueFrom(this.http.get<string[]>('/api/user/permissions'));
  // }

  search(criteria?: any) {
    return firstValueFrom(this.http.post<{ List: any[], Total: number }>('/api/user/search', criteria || {}));
  }

  get(id: string) {
    return firstValueFrom(this.http.get<any>('/api/user/get/' + id));
  }

  save(user: any) {
    return firstValueFrom(this.http.post<void>('/api/user/save', user));
  }

  delete(id: string, permanent?: boolean) {
    return firstValueFrom(this.http.delete<void>('/api/user/delete/' + id + (permanent ? '/true' : '')));
  }

  saveProfile(profile: any) {
    return firstValueFrom(this.http.post<void>('/api/user/save/profile', profile));
  }

  savePhoneNumber(phoneNumber: string) {
    return firstValueFrom(this.http.post<void>('/api/user/save/phone-number', { phoneNumber }));
  }

  getPaymentData(mode: 'balance', amount: number) {
    return firstValueFrom(this.http.get<PaymentData>(`/api/user/payment-data/${mode}/${amount}`));
  }
}
